<!-- =========================================================================================
    File Name: OnboardingCarousel.vue
    Description: Reusable carousel component for onboarding cards
    ----------------------------------------------------------------------------------------
    Item Name: TCVelik - Onboarding Platform
    Author: TCVelik Team
========================================================================================== -->

<template>
  <div class="onboarding-carousel-wrapper">
    <h2 v-if="title" class="carousel-title">{{ title }}</h2>
    <vx-card class="carousel-card">
        <swiper :options="swiperOption" class="onboarding-carousel">
          <swiper-slide v-for="onboarding in onboardings" :key="onboarding.id" class="carousel-slide">
            <div class="onboarding-card" @click="handleCardClick(onboarding)">
              <div class="card-image" v-if="onboarding.firstImage"  >
                <img :src="onboarding.firstImage" :alt="onboarding.name" />
              </div>
              <div class="card-content">
                <h3 class="card-title">{{ onboarding.name }}</h3>
                <p class="card-description">{{ onboarding.description }}</p>
                <div class="card-stats" v-if="onboarding.currentUserStats">
                  <span class="status-badge" :class="getStatusClass(onboarding.currentUserStats)">
                    {{ $t(onboarding.currentUserStats )}}
                  </span>
                </div>
              </div>
            </div>
          </swiper-slide>
          <div class="swiper-pagination" slot="pagination"></div>
        </swiper>
    </vx-card>
  </div>
</template>

<script>
import 'swiper/dist/css/swiper.min.css'
import { swiper, swiperSlide } from 'vue-awesome-swiper'

export default {
  name: 'OnboardingCarousel',
  components: {
    swiper,
    swiperSlide
  },
  props: {
    onboardings: {
      type: Array,
      required: true,
      default: () => []
    },
    title: {
      type: String,
      default: ''
    },
    autoplay: {
      type: Boolean,
      default: true
    },
    autoplayDelay: {
      type: Number,
      default: 5000
    },
    slidesPerView: {
      type: Number,
      default: 1
    },
    spaceBetween: {
      type: Number,
      default: 30
    }
  },
  computed: {
    swiperOption() {
      return {
        slidesPerView: this.slidesPerView,
        spaceBetween: this.spaceBetween,
        centeredSlides: true,
        autoplay: this.autoplay ? {
          delay: this.autoplayDelay,
          disableOnInteraction: false
        } : false,
        pagination: {
          el: '.swiper-pagination',
          clickable: true
        },
        breakpoints: {
          480: {
            slidesPerView: 1,
            spaceBetween: 10
          },
          768: {
            slidesPerView: Math.min(2, this.slidesPerView),
            spaceBetween: 20
          },
          1024: {
            slidesPerView: Math.min(3, this.slidesPerView),
            spaceBetween: 30
          }
        }
      }
    }
  },
  methods: {
    getStatusClass(status) {
      switch(status) {
        case 'COMPLETED':
          return 'status-completed';
        case 'IN_PROGRESS':
          return 'status-progress';
        case 'NOT_STARTED':
          return 'status-not-started';
        default:
          return 'status-default';
      }
    },
    handleCardClick(onboarding) {
      this.$emit('card-click', onboarding);
    }
  }
}
</script>

<style lang="scss" scoped>
.onboarding-carousel-wrapper {
  margin-bottom: 2rem;
}

.carousel-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 1rem;
}

.carousel-card {
  margin-bottom: 2rem;
}

.onboarding-carousel {
  height: 400px;
}

.carousel-slide {
  height: 100%;
  padding: 10px;
  display: flex;
  align-items: stretch;
}

.onboarding-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  height: 100%;
  min-height: 350px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  width:100%;
}

.onboarding-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
}

.card-image {
  height: 200px;
  overflow: hidden;
  position: relative;
}

.card-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.onboarding-card:hover .card-image img {
  transform: scale(1.05);
}

  .card-content {
    padding: 20px;
    width: 300px;
    flex: 1;
    display: flex;
    flex-direction: column;
  }

.card-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #2c3e50;
  margin: 0 0 10px 0;
  line-height: 1.3;
}

.card-description {
  color: #6c757d;
  font-size: 0.9rem;
  line-height: 1.5;
  margin: 0 0 15px 0;
  flex: 1;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.card-stats {
  margin-top: auto;
}

.status-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.status-completed {
  background-color: #d4edda;
  color: #155724;
}

.status-progress {
  background-color: #fff3cd;
  color: #856404;
}

.status-not-started {
  background-color: #f8d7da;
  color: #721c24;
}

.status-default {
  background-color: #e2e3e5;
  color: #383d41;
}

/* Swiper Pagination Styles */
.swiper-pagination-bullet {
  background: #7367f0;
  opacity: 0.3;
}

.swiper-pagination-bullet-active {
  opacity: 1;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .onboarding-carousel {
    height: 450px;
  }
  
  .carousel-slide {
    padding: 5px;
  }
  
  .onboarding-card {
    min-height: 400px;
  }
  
  .card-image {
    height: 180px;
  }
  
  .card-content {
    padding: 15px;
    flex: 1;
  }
  
  .card-title {
    font-size: 1.2rem;
    margin-bottom: 8px;
  }
  
  .card-description {
    font-size: 0.95rem;
    line-height: 1.4;
    margin-bottom: 12px;
  }
  
  .status-badge {
    font-size: 0.8rem;
    padding: 6px 14px;
  }
}

@media (max-width: 480px) {
  .onboarding-carousel {
    height: 500px;
  }
  
  .carousel-slide {
    padding: 3px;
  }
  
  .onboarding-card {
    min-height: 450px;
  }
  
  .card-image {
    height: 200px;
  }
  
  .card-content {
    padding: 18px;
  }
  
  .card-title {
    font-size: 1.3rem;
    margin-bottom: 10px;
  }
  
  .card-description {
    font-size: 1rem;
    line-height: 1.5;
    margin-bottom: 15px;
    -webkit-line-clamp: 4;
  }
  
  .status-badge {
    font-size: 0.85rem;
    padding: 8px 16px;
  }
}
</style> 
